/**
 * Academic Content API Client
 *
 * The student-facing half of the content library. Reading is open to any
 * logged-in user -- no subscription gate, no role check -- so there is nothing
 * here that inspects the user's plan.
 *
 * Follows the existing wiziQuizApiClient pattern.
 */

import * as http from '../components/http.js';
import { API_BASE_URL } from '../components/apiConfig.js';

export interface AcademicContentItem {
  id: number;
  title: string;
  description: string;
  exam_id: number;
  class_group_id: number;
  subject_id: number;
  content_type_id: number;
  /** null means the item covers the whole subject rather than one chapter. */
  chapter_id: number | null;
  file_url: string;
  /** What file_url holds: 'pdf' or 'image'. Per item, not per content type. */
  file_type: string;
  video_url: string;
  /** Card preview. Empty means fall back to the content-type icon. */
  thumbnail_url: string;
  file_size_bytes: number | null;
  is_active: number;
  created_at: string;

  // Resolved server-side so a card can render without the lookup tables.
  exam_name: string;
  class_group: string;
  subject: string;
  content_type: string;
  chapter_name: string;
  /** 'pdf' or 'video' -- which URL to open. */
  media: string;

  /**
   * 'wizi' for WiZi's library, 'school' for the student's own school's. Ids
   * repeat across the two, so a card is keyed on source + id.
   */
  source: 'wizi' | 'school';
  /** The teacher a school item is listed under. null = the school itself. */
  teacher_id: number | null;
  teacher_name: string;
}

export interface LookupOption {
  id: number;
  [key: string]: any;
}

export interface ContentFilterOptions {
  exam: LookupOption[];
  class_group: LookupOption[];
  subject: LookupOption[];
  /** Carries subject_id, so chapters are narrowed client-side. */
  chapter: LookupOption[];
  content_type: LookupOption[];
  /** Teachers with published school material. Empty outside a school. */
  teacher: { id: number; name: string }[];
  /** Published items in the student's school. 0 hides the school tab. */
  school_total: number;
}

export type ContentSource = 'wizi' | 'school';

export interface ContentQuery {
  /** Which library. Omitted means WiZi's, as it always has. */
  source?: ContentSource;
  exam_id?: number | string;
  class_group_id?: number | string;
  subject_id?: number | string;
  content_type_id?: number | string;
  chapter_id?: number | string;
  /** School library only. */
  teacher_id?: number | string;
  title?: string;
  page?: number;
  pagesize?: number;
}

/**
 * Every lookup list in one request, so the filter bar can be drawn in a single
 * round trip rather than five.
 */
export const getContentFilters = async () => {
  const api = `${API_BASE_URL}/academic-content/filters`;
  return http.get(api).then((response: any) => {
    return response;
  }).catch((error: any) => {
    console.error('Get Content Filters API Error:', error);
    throw error;
  });
};

/**
 * One page of content, narrowed by whatever filters are set.
 *
 * The API expects react-admin's filter shape -- {"exam_id=": 3} -- because the
 * same endpoint serves the admin grid. The "=" suffixes are built here so
 * callers pass plain field names.
 */
export const getAcademicContent = async (query: ContentQuery = {}) => {
  const { page = 1, pagesize = 12, title, source, ...ids } = query;

  const filter: Record<string, any> = {};
  Object.keys(ids).forEach((key) => {
    const value = (ids as any)[key];
    // '' is what an unselected dropdown sends. Sending it through would filter
    // on the empty string and match nothing.
    if (value !== undefined && value !== null && value !== '') {
      filter[`${key}=`] = value;
    }
  });
  if (title) {
    filter['title'] = title;
  }

  const params = new URLSearchParams({
    page: String(page),
    pagesize: String(pagesize),
    sortby: 'created_at',
    sortorder: 'desc',
    filter: JSON.stringify(filter)
  });
  if (source) {
    params.set('source', source);
  }

  const api = `${API_BASE_URL}/academic-content?${params.toString()}`;
  return http.get(api).then((response: any) => {
    return response;
  }).catch((error: any) => {
    console.error('Get Academic Content API Error:', error);
    throw error;
  });
};

export const getAcademicContentItem = async (id: number) => {
  const api = `${API_BASE_URL}/academic-content/${id}`;
  return http.get(api).then((response: any) => {
    return response;
  }).catch((error: any) => {
    console.error('Get Academic Content Item API Error:', error);
    throw error;
  });
};

/** The API answers with status 'success' on the happy path. */
export const isContentSuccess = (response: any): boolean =>
  !!response && response.status === 'success';

export const getContentErrorMessage = (response: any): string =>
  (response && response.message) || 'Something went wrong. Please try again.';

/** Bytes as something a student can judge before opening on mobile data. */
export const formatFileSize = (bytes: number | null): string => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};
